/*
  Provides an API for speaking sounds.
  This is a TypeScript API to meSpeak (see https://www.masswerk.at/mespeak/),
  which is a JavaScript wrapper around eSpeak (see http://espeak.sourceforge.net/).
*/

declare global {
  namespace meSpeak {
    /** s is either in the current language, or in synth phonemes if wrapped in [[string]] */
    function speak(s: string, options?: meSpeakOptions, callback?: SpeakCallback): number;  // returns id
    function play(ad: AudioData, volume?: number, callback?: PlayCallback, id?: number, pan?: number): number;
    function stop(id?: number): void;

    function loadVoice(url: string, callback?: LoadCallback): void;
    function isVoiceLoaded(voice: string): boolean;
    function setDefaultVoice(voice: string): void;
    function getDefaultVoice(): string;

    function loadConfig(url: string): void;
    function loadCustomConfig(url: string): void;
    function isConfigLoaded(): boolean;

    /** whether it's running with a concurrent worker or single-threaded instance */
    function getRunMode(): 'worker' | 'instance';
    function restartWithInstance(): void; // need to call loadVoice(voice) afterwards

    function getAudioAnalyser(): AnalyserNode;
    function setFilter(filter1: Filter, filter2?: Filter, filter3?: Filter): void;

    /** wav data based on options.rawdata */
    type AudioData = unknown;
    type Filter = BiquadFilterNode | DynamicsCompressorNode;

    type PlayCallback = (success: boolean) => void;
    type SpeakCallback = (success: boolean, id: number, stream: AudioData) => void;
    type LoadCallback = (success: boolean, idOrError: number | unknown) => void;
  }
}

export interface meSpeakOptions {
  // eSpeak options

  /** loudness, default: 100 */
  amplitude?: number;
  /** voice pitch, default: 50 */
  pitch?: number;
  /** words per minute, default: 175 */
  speed?: number;
  /** default: last voice used, e.g., 'en/en' */
  voice?: unknown;
  /** additional 10 ms gaps between words, default: 0 */
  wordgap?: number;
  /** variant from espeak-data/voices/!v */
  variant?: SpeakVariant;
  /** default: 0 */
  linebreak?: number;
  /** indicate word starting w/ capital using...
   * 1: click (double-click for all-caps)
   * 2: say "capital" first
   * 3+: increase the pitch by specified amount */
  capitals?: number;
  /** true: speak the name of punctuation,
   * string: limit to just the specified punctuation */
  punct?: boolean | string;
  /** remove pause at end of sentences */
  nostop?: boolean;
  /** input is UTF-16, default: UTF-8 */
  utf16?: boolean;
  /** text contains SSML or other XML tags (limited HTML) */
  ssml?: boolean;

  // additional meSpeak options

  /** volume relative to global volume in (0..1), default: 1 */
  volume?: number;
  /** send compiled eSpeak-commands to the JS console */
  log?: boolean;
  /** stereo panning in (-1..1), from extreme left to right, default: 0 */
  pan?: number;
  /** how to encode audio data (wav), which can be passed to play() */
  rawdata?: true | 'base64' | 'mime' | 'array';

  // custom settings for how to call meSpeak

  /** true: speak immediately, needed for platforms such as iPhones,
   * false: break into pieces & defer to work around flakiness (default)
   * - though it may be that playing sound (playAudio) is what actually fixes the problem */
  immediate?: boolean;
}

export type SpeakVariant =
  | "betty" | "fiona" | "hugh" | "martin" // custom
  | "f1" | "f2" | "f3" | "f4" | "f5" // female voices
  | "m1" | "m2" | "m3" | "m4" | "m5" | "m6" | "m7" // male voices
  | "croak" | "klatt" | "klatt2" | "klatt3" | "whisper" | "whisperf";

export const allSpeakVariants = [
  "betty", "fiona", "hugh", "martin",
  "f1", "f2", "f3", "f4", "f5",
  "m1", "m2", "m3", "m4", "m5", "m6", "m7",
  "croak", "klatt", "klatt2", "klatt3", "whisper", "whisperf",
];
